import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { Worker } from 'app/_models/worker.model';
import { ContractorService } from 'app/_services/contractor.service';
import { NotificationService } from 'app/_services/notification.service';
import { SiteWorkersService } from 'app/_services/site-workers.service';
import { TabItemHelperService } from 'app/_services/tab-item-helper.service';
import { WorkerService } from 'app/_services/worker.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'contractor-worker-page',
  templateUrl: './contractor-worker-page.component.html'
})
export class ContractorWorkerPageComponent implements OnInit {
  public tabitems = [];
  public worker: Worker;
  public routeRole: any;
  public workerIsApprovedOnSite = undefined;
  private siteOwnerId: any;
  private selectedWorkerId: any;
  public isAdmin: boolean;
  public contractorsOptionsList: any [] = [];

  public isPowerStationOwnerView = false;
  public isPowerStationManagerView = false;
  public backLink: string;
  public isSiteOwner: boolean;
  public isSiteDelegate: boolean;

  public editUserForm: FormGroup;
  public editUserModal = false;

  public showSwitchContractorModal: boolean = false;
  public selectedContractor;
  public saveInProgress: boolean = false;
  public isAuthorisingOfficer: boolean = false;

  constructor(
    private siteWorkersService: SiteWorkersService,
    private workerService: WorkerService,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private tabItemHelperService: TabItemHelperService,
    private contractorService: ContractorService,
    private router: Router,
    private globals: Globals,
    private fb: FormBuilder,
  ) {
    this.routeRole = globals.user.routeRole;
    this.isAdmin = (this.routeRole === 'admin' || this.routeRole === 'support');
    this.isSiteOwner = (this.routeRole === 'site-owner');
    this.isSiteDelegate = (this.routeRole === 'site-delegate');
    this.isAuthorisingOfficer = (this.routeRole === 'authorising-officer');

    this.isPowerStationOwnerView = this.routeRole === 'power-station-owner';
    this.isPowerStationManagerView = this.routeRole === 'power-station-manager';

    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedWorkerId = params.workerId;
    });

    this.activatedRoute.queryParams.subscribe((params: Params) => {
      this.backLink = params.backLink;
      this.siteOwnerId = this.siteOwnerId ? this.siteOwnerId : parseInt(params.siteOwnerId);
      this.tabitems = this.tabItemHelperService.getWorkerSectionTabItems(this.routeRole, this.backLink, this.selectedWorkerId, this.siteOwnerId);
    });

    this.editUserForm = fb.group({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required]),
      phoneNumber: new FormControl('', [Validators.required, Validators.minLength(8)]),
    });
  }

  ngOnInit() {
    this.setupPage();
  }

  private setupPage() {
    this.workerService.getWorkerById(this.selectedWorkerId).subscribe(worker => {
      if (!worker.deleted || this.isAdmin) {
        this.worker = worker;

        if (this.isPowerStationOwnerView || this.isPowerStationManagerView) {
          this.checkOnMainSite();
        }
      } else {
        this.notificationService.notify('warning', `Cannot find this worker`);
        this.router.navigate([this.backLink]);
        return;
      }
    });
  }

  private checkOnMainSite() {
    const siteId = this.globals.powerStationOwner.mainSiteId;
    const filters = [
      { key: 'firstName', value: this.worker.firstName },
      { key: 'lastName', value: this.worker.lastName },
      { key: 'company', value: this.worker.contractor.name }
    ];

    const pagination = { size: 1_000_000, page: 0 };

    this.siteWorkersService.getAllWorkerForSiteWithFilters(siteId, pagination, undefined, filters).subscribe((data: any) => {
      const siteWorkers = data.body;
      this.workerIsApprovedOnSite = siteWorkers.some(siteWorker => this.worker.id === siteWorker.workerUser.id);
    });
  }

  public changePassword() {
    (Swal as any)
      .mixin({
        input: 'text',
        confirmButtonText: 'Save',
        showCancelButton: true,
      })
      .queue([
        {
          title: 'Enter New Password',
          text: 'Please enter a new password.',
        },
      ])
      .then((result) => {
        if (result.value) {
          Swal.fire({
            title: 'Confirm',
            html: 'New Password: <pre><code>' + JSON.stringify(result.value[0]) + '</code></pre>',
            confirmButtonText: 'Confirm',
            showCancelButton: true,
          }).then((res) => {
            if (res.value) {
              const body = {
                newPassword: result.value[0],
                skylarkUserId: this.worker.id,
              };
              this.contractorService.updateWorkerPin(body).subscribe((response) => {
                this.notificationService.notify('success', 'Worker Password Changed', 'Success')
              });
            }
          });
        }
      });
  }

  public editUser() {
      this.editUserForm.patchValue({
        firstName: this.worker.firstName,
        lastName: this.worker.lastName,
        email: this.worker.email,
        phoneNumber: this.worker.phoneNumber,
      });
    this.toggleEditUserModal();
  }

  public toggleEditUserModal() {
    this.editUserModal = !this.editUserModal;
  }

  public onEditUserFormSubmit() {
    const values = this.editUserForm.value;
    const body = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      phoneNumber: values.phoneNumber.trim(),
      workOrderAdmin: false,
      skylarkUserId: this.worker.id,
    };
    this.contractorService.editWorker(body).subscribe((data: any) => {
      this.toggleEditUserModal();
      this.setupPage();

      this.editUserForm.reset();
      this.notificationService.notify('success', 'Worker updated');
    });
  }

  public deleteUser() {
    Swal.fire({
      title: 'Soft Delete Worker User?',
      text: 'Are you sure?',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete!',
    }).then((result) => {
      if (result.value) {
        this.workerService.softDeleteWorkerUserBySkylarkId(this.worker.id).subscribe(result => {

          this.setupPage();
          this.notificationService.notify('success', 'Worker User soft deleted ');
        });
      }
    });
  }
  public switchContractor() {
    this.showSwitchContractorModal = true;
    this.saveInProgress = false;
    this.contractorService.getAllContractorFullList().subscribe((data: any) => {
      this.contractorsOptionsList = [
        { label: 'Select a company', value: null },
        ...data.map((element) => ({
          label: element.name,
          value: element.id,
        }))];
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', 'Error retrieving contractor list');
    });
  }

  public onSwitchContractorSubmit() {
    this.showSwitchContractorModal = true;
    this.saveInProgress = true;

    this.workerService.changeContractor(this.selectedWorkerId, this.selectedContractor).subscribe((result: any) => {
      this.setupPage();
      let name = result.firstName + ' ' + result.lastName
      this.notificationService.notify('success', name + ' has been moved to ' + result.maintenance.name);
      this.showSwitchContractorModal = false;
      this.saveInProgress = false;
    });
   }



}
